// Custom AttachmentAdapter — files are uploaded ahead of time via a real
// multipart `POST /artifacts/uploads` call, then referenced in the outgoing
// message by that artifact's own download URL. `nativeMessageAdapters.ts`
// and `nativeStream.ts` convert assistant-ui file parts into native document input with
// `source.type: "url"`. The backend never fetches that URL — it only parses
// the artifact id back out of it and resolves an already-stored row (see
// `agenthub.services.chat.native_intake._content_part_artifact_id`), so this carries
// no open-proxy risk despite looking like a "URL-sourced attachment".
//
// A brand-new conversation's native stream client already has a real, usable
// `threadId` at construction time, so uploading before the
// first chat message is sent is safe: the backend's `thread_repo.get_or_create`
// is documented to accept that exact client-generated id on first use (see
// `agenthub/repositories/chat/thread.py`).
//
// Follows https://www.assistant-ui.com/docs/guides/attachments:
// - `add()` is an async generator (progress updates) — yields `running/uploading`
//   immediately, awaits the upload, then yields `requires-action/composer-send`
//   with the reference content already attached.
// - Rejections (validation or a failed upload) throw from `add()`; SendComposer
//   surfaces them via the `composer.attachmentAddError` event.
// - `currentCount` for the count guardrail is tracked via `pendingIds` (an
//   adapter instance's own closure state).

import type { AttachmentAdapter, CompleteAttachment } from '@assistant-ui/react';
import { artifactDownloadUrl, isImageFile, validateFiles } from '@/entities/file';
import { buildRequestTarget } from '@/shared/api';
import { getToken } from '@/shared/auth';
import type { UploadLimits } from '@/app/providers/CapabilitiesProvider';

export interface AttachmentUploadContext {
  baseUrl: string;
  agentId: string;
  /** Read fresh at upload time — the same `HttpAgent` instance's `.threadId`. */
  getThreadId: () => string;
}

interface UploadedArtifact {
  artifact_id: string;
  filename: string;
  content_type: string;
}

async function uploadFile(file: File, ctx: AttachmentUploadContext): Promise<UploadedArtifact> {
  const form = new FormData();
  form.append('files', file);
  form.append('thread_id', ctx.getThreadId());
  form.append('agent_id', ctx.agentId);

  const token = getToken();
  const response = await fetch(buildRequestTarget(ctx.baseUrl, '/api/v1/artifacts/uploads'), {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new Error((problem?.detail as string | undefined) ?? `Upload failed for "${file.name}"`);
  }
  const body = (await response.json()) as { uploaded_files?: UploadedArtifact[] };
  const uploaded = body.uploaded_files?.[0];
  if (!uploaded) throw new Error(`Upload failed for "${file.name}"`);
  return uploaded;
}

export function createAgenthubAttachmentAdapter(
  getUploadLimits: () => UploadLimits | undefined,
  uploadContext: AttachmentUploadContext,
): AttachmentAdapter {
  const pendingIds = new Set<string>();

  return {
    accept: '*',

    async *add({ file }) {
      const limits = getUploadLimits();
      if (limits) {
        const { rejected } = validateFiles([file], {
          currentCount: pendingIds.size,
          allowedExtensions: limits.allowed_extensions,
          maxBytes: limits.max_bytes,
          maxCount: limits.max_count,
        });
        if (rejected.length > 0) {
          throw new Error(rejected[0].reasons.map((r) => r.userMessage).join(' '));
        }
      }

      const id = `${file.name}-${file.size}-${file.lastModified}`;
      pendingIds.add(id);
      // `image` only picks the sent message's thumbnail renderer (ChatMessage.tsx);
      // every upload still sends as a `file` part that references the artifact.
      const type = isImageFile(file.type, file.name) ? ('image' as const) : ('file' as const);
      const base = { id, type, name: file.name, contentType: file.type, file };

      yield { ...base, status: { type: 'running' as const, reason: 'uploading' as const, progress: 0 } };

      const uploaded = await uploadFile(file, uploadContext);
      const downloadUrl = artifactDownloadUrl(uploadContext.baseUrl, uploaded.artifact_id);

      yield {
        ...base,
        status: { type: 'requires-action' as const, reason: 'composer-send' as const },
        content: [
          {
            type: 'file' as const,
            filename: uploaded.filename,
            data: downloadUrl,
            mimeType: uploaded.content_type || file.type || 'application/octet-stream',
          },
        ],
      };
    },

    async remove(attachment) {
      pendingIds.delete(attachment.id);
    },

    async send(attachment): Promise<CompleteAttachment> {
      return { ...attachment, status: { type: 'complete' }, content: attachment.content ?? [] };
    },
  };
}
